/**
 * Facebook Messenger Platform - Customer Feedback Webhook Types
 * 
 * These types represent the webhook event structure for messaging_feedback events.
 * Triggered when a user submits feedback through a Customer Feedback Template.
 * 
 * @see https://developers.facebook.com/docs/messenger-platform/send-messages/templates/customer-feedback-template
 */

import {
  BaseWebhookEvent,
  WebhookEventType,
  WebhookPayload,
} from './base-types';

/**
 * Available feedback types for customer feedback templates
 */
export enum FeedbackType {
  /** Customer Satisfaction - Score range 1-5 */
  CSAT = 'csat',
  /** Net Promoter Score - Score range 0-10 */
  NPS = 'nps',
  /** Customer Effort Score - Score range 1-7 */
  CES = 'ces',
}

/**
 * Available display options for CSAT feedback type
 */
export enum CSATDisplayOption {
  /** Numeric scale from 1 to 5 */
  ONE_TO_FIVE = 'one_to_five',
  /** Five star rating display */
  FIVE_STARS = 'five_stars',
  /** Five emoji rating display */
  FIVE_EMOJIS = 'five_emojis',
}

/**
 * Available display options for NPS feedback type
 */
export enum NPSDisplayOption {
  /** Numeric scale from 0 to 10 */
  ZERO_TO_TEN = 'zero_to_ten',
}

/**
 * Available display options for CES feedback type
 */
export enum CESDisplayOption {
  /** Numeric scale from 1 to 7 */
  ONE_TO_SEVEN = 'one_to_seven',
}

/**
 * Follow-up feedback type for optional text input
 */
export enum FollowUpType {
  /** Free-form text input (max 400 characters) */
  FREE_FORM = 'free_form',
}

// Note: MessagingFeedbackSender and MessagingFeedbackRecipient are now replaced by
// WebhookSender and WebhookRecipient from base-types.ts
// Keeping these as deprecated aliases for backward compatibility

import { WebhookSender as MessagingFeedbackSender } from './base-types';
import { WebhookRecipient as MessagingFeedbackRecipient } from './base-types';
export { MessagingFeedbackSender, MessagingFeedbackRecipient };

/**
 * Represents optional follow-up feedback data
 */
export interface FeedbackFollowUp {
  /** Type of follow-up feedback - currently only supports free_form */
  type: FollowUpType.FREE_FORM;
  
  /** 
   * User-provided text feedback.
   * Limited to 400 characters maximum.
   */
  payload: string;
}

/**
 * Represents individual question feedback data within a screen
 */
export interface FeedbackQuestion {
  /** 
   * Type of feedback question (CSAT, NPS, or CES).
   * Determines the scoring range and display format.
   */
  type: FeedbackType;
  
  /** 
   * Numeric score provided by the user.
   * Range depends on feedback type:
   * - CSAT: 1-5
   * - NPS: 0-10  
   * - CES: 1-7
   */
  payload: string;
  
  /** 
   * Optional follow-up text feedback from the user.
   * Only present if the template included a text input field.
   */
  follow_up?: FeedbackFollowUp;
}

/**
 * Represents a feedback screen containing questions and responses
 */
export interface FeedbackScreen {
  /** 
   * Screen identifier within the feedback template.
   * Typically 0 for single-screen templates.
   */
  screen_id: number;
  
  /** 
   * Map of question IDs to their corresponding feedback responses.
   * Question IDs are defined when creating the feedback template.
   */
  questions: Record<string, FeedbackQuestion>;
}

/**
 * Main messaging feedback data structure
 */
export interface MessagingFeedbackData {
  /** 
   * Array of feedback screens with user responses.
   * Each screen contains questions and their answers.
   */
  feedback_screens: FeedbackScreen[];
}

/**
 * Main webhook event structure for messaging feedback with discriminator
 * 
 * This event is triggered when a user submits feedback through a 
 * Customer Feedback Template. The webhook provides the user's
 * scores and optional text feedback.
 * 
 * @example
 * ```json
 * {
 *   "type": "messaging_feedback",
 *   "sender": {
 *     "id": "1234567890123456"
 *   },
 *   "recipient": {
 *     "id": "9876543210987654"
 *   },
 *   "timestamp": 1458668856463,
 *   "messaging_feedback": {
 *     "feedback_screens": [{
 *       "screen_id": 0,
 *       "questions": {
 *         "satisfaction_q1": {
 *           "type": "csat",
 *           "payload": "4",
 *           "follow_up": {
 *             "type": "free_form",
 *             "payload": "Good service overall!"
 *           }
 *         }
 *       }
 *     }]
 *   }
 * }
 * ```
 */
export interface MessagingFeedbackWebhookEvent extends BaseWebhookEvent {
  /** Discriminator for type narrowing */
  type: WebhookEventType.MESSAGING_FEEDBACK;
  
  /** The actual feedback data containing user responses */
  messaging_feedback: MessagingFeedbackData;
}

/**
 * Complete webhook payload structure that includes the messaging feedback event
 * along with other webhook metadata
 */
export interface MessagingFeedbackWebhookPayload extends WebhookPayload<MessagingFeedbackWebhookEvent> {}

/**
 * Type guard to check if a webhook event is a messaging feedback event
 * 
 * @param event - The webhook event to check
 * @returns True if the event contains a messaging_feedback property
 * 
 * @example
 * ```typescript
 * if (isMessagingFeedbackEvent(event)) {
 *   // TypeScript now knows event has messaging_feedback property
 *   console.log(`Received feedback with ${event.messaging_feedback.feedback_screens.length} screens`);
 * }
 * ```
 */
export function isMessagingFeedbackEvent(event: any): event is MessagingFeedbackWebhookEvent {
  return event && typeof event === 'object' && 'messaging_feedback' in event;
}

/**
 * Utility type for extracting just the feedback data from a webhook event
 */
export type MessagingFeedbackData_Extract = MessagingFeedbackData;

/**
 * Utility type for common feedback properties that might be used in processing
 */
export interface MessagingFeedbackProcessingContext {
  /** The user who submitted the feedback */
  senderId: string;
  
  /** The page that received the feedback */
  recipientId: string;
  
  /** When the feedback was submitted */
  submissionTimestamp: number;
  
  /** Total number of feedback screens */
  screenCount: number;
  
  /** All question responses flattened from all screens */
  allResponses: Array<{
    questionId: string;
    feedbackType: FeedbackType;
    score: number;
    textFeedback?: string;
    screenId: number;
  }>;
}

/**
 * Helper function to extract processing context from a messaging feedback event
 * 
 * @param event - The messaging feedback webhook event
 * @returns Simplified processing context with flattened responses
 * 
 * @example
 * ```typescript
 * const context = extractMessagingFeedbackContext(webhookEvent);
 * console.log(`User ${context.senderId} submitted ${context.allResponses.length} feedback responses`);
 * context.allResponses.forEach(response => {
 *   console.log(`${response.questionId}: ${response.score}/10 (${response.feedbackType})`);
 * });
 * ```
 */
export function extractMessagingFeedbackContext(event: MessagingFeedbackWebhookEvent): MessagingFeedbackProcessingContext {
  const allResponses: MessagingFeedbackProcessingContext['allResponses'] = [];
  
  // Flatten all responses from all screens
  event.messaging_feedback.feedback_screens.forEach(screen => {
    Object.entries(screen.questions).forEach(([questionId, question]) => {
      allResponses.push({
        questionId,
        feedbackType: question.type,
        score: parseInt(question.payload, 10),
        textFeedback: question.follow_up?.payload,
        screenId: screen.screen_id,
      });
    });
  });
  
  return {
    senderId: event.sender.id,
    recipientId: event.recipient.id,
    submissionTimestamp: event.timestamp,
    screenCount: event.messaging_feedback.feedback_screens.length,
    allResponses,
  };
}

/**
 * Helper function to get feedback scores by type from an event
 * 
 * @param event - The messaging feedback webhook event
 * @returns Map of feedback types to their scores
 * 
 * @example
 * ```typescript
 * const scores = getFeedbackScoresByType(webhookEvent);
 * const csatScore = scores.get(FeedbackType.CSAT); // number | undefined
 * const npsScore = scores.get(FeedbackType.NPS);   // number | undefined
 * ```
 */
export function getFeedbackScoresByType(event: MessagingFeedbackWebhookEvent): Map<FeedbackType, number[]> {
  const scoresByType = new Map<FeedbackType, number[]>();
  
  event.messaging_feedback.feedback_screens.forEach(screen => {
    Object.values(screen.questions).forEach(question => {
      const score = parseInt(question.payload, 10);
      const existingScores = scoresByType.get(question.type) || [];
      scoresByType.set(question.type, [...existingScores, score]);
    });
  });
  
  return scoresByType;
}

/**
 * Helper function to extract all text feedback from an event
 * 
 * @param event - The messaging feedback webhook event
 * @returns Array of text feedback strings
 * 
 * @example
 * ```typescript
 * const textFeedback = extractTextFeedback(webhookEvent);
 * textFeedback.forEach(feedback => {
 *   console.log(`User comment: "${feedback}"`);
 * });
 * ```
 */
export function extractTextFeedback(event: MessagingFeedbackWebhookEvent): string[] {
  const textFeedback: string[] = [];
  
  event.messaging_feedback.feedback_screens.forEach(screen => {
    Object.values(screen.questions).forEach(question => {
      if (question.follow_up?.payload) {
        textFeedback.push(question.follow_up.payload);
      }
    });
  });
  
  return textFeedback;
}

/**
 * Constants related to customer feedback constraints and limits
 */
export const MESSAGING_FEEDBACK_CONSTANTS = {
  /** Maximum characters allowed in free-form text feedback */
  MAX_TEXT_FEEDBACK_LENGTH: 400,
  
  /** Score ranges for different feedback types */
  SCORE_RANGES: {
    [FeedbackType.CSAT]: { min: 1, max: 5 },
    [FeedbackType.NPS]: { min: 0, max: 10 },
    [FeedbackType.CES]: { min: 1, max: 7 },
  },
  
  /** Template expiry constraints */
  TEMPLATE_EXPIRY: {
    /** Minimum expiry days */
    MIN_DAYS: 1,
    /** Maximum expiry days */
    MAX_DAYS: 7,
    /** Default expiry days */
    DEFAULT_DAYS: 1,
  },
  
  /** Question ID constraints */
  QUESTION_ID: {
    /** Maximum length for question IDs */
    MAX_LENGTH: 80,
    /** Valid characters pattern (alphanumeric) */
    VALID_PATTERN: /^[a-zA-Z0-9_]+$/,
  },
  
  /** Template limits */
  TEMPLATE_LIMITS: {
    /** Maximum number of titles per template */
    MAX_TITLES: 1,
    /** Maximum number of scoring components per template */
    MAX_SCORING_COMPONENTS: 1,
  },
  
  /** Webhook event type identifier */
  EVENT_TYPE: 'messaging_feedback' as const,
} as const;

/**
 * Validation helper to check if a score is valid for a given feedback type
 * 
 * @param feedbackType - The type of feedback being validated
 * @param score - The score to validate
 * @returns True if the score is within the valid range for the feedback type
 * 
 * @example
 * ```typescript
 * const isValid = isValidFeedbackScore(FeedbackType.CSAT, 4); // true
 * const isInvalid = isValidFeedbackScore(FeedbackType.NPS, 15); // false
 * ```
 */
export function isValidFeedbackScore(feedbackType: FeedbackType, score: number): boolean {
  const range = MESSAGING_FEEDBACK_CONSTANTS.SCORE_RANGES[feedbackType];
  return Number.isInteger(score) && score >= range.min && score <= range.max;
}

/**
 * Validation helper to check if a question ID is valid
 * 
 * @param questionId - The question ID to validate
 * @returns True if the question ID meets the format requirements
 * 
 * @example
 * ```typescript
 * const isValid = isValidQuestionId('satisfaction_q1'); // true
 * const isInvalid = isValidQuestionId('invalid-id!'); // false
 * ```
 */
export function isValidQuestionId(questionId: string): boolean {
  return questionId.length <= MESSAGING_FEEDBACK_CONSTANTS.QUESTION_ID.MAX_LENGTH &&
         MESSAGING_FEEDBACK_CONSTANTS.QUESTION_ID.VALID_PATTERN.test(questionId);
}

/**
 * Validation helper to check if text feedback is within character limits
 * 
 * @param textFeedback - The text feedback to validate
 * @returns True if the text is within the character limit
 * 
 * @example
 * ```typescript
 * const isValid = isValidTextFeedback('Great service!'); // true
 * const isInvalid = isValidTextFeedback('a'.repeat(500)); // false
 * ```
 */
export function isValidTextFeedback(textFeedback: string): boolean {
  return textFeedback.length <= MESSAGING_FEEDBACK_CONSTANTS.MAX_TEXT_FEEDBACK_LENGTH;
}